import { MAX_LOOKALIKE_DOMAINS } from "@signalist/shared-types";
import { z } from "zod";

import { MetaDataSchema } from "@/schemas/meta-data";
import { CompanyEnrichType, NewInRoleChangeType } from "@/enum/company-enrich-type.enum";
import { ERROR_TYPE } from "@/utils";

const { REQUIRED } = ERROR_TYPE;

// Domain count limits. The lookalike seed limit is shared with the API (PREC-4374).
export { MAX_LOOKALIKE_DOMAINS };
export const MAX_COMPANY_DOMAINS = 100;

// New-in-role relative window — see docs/company-enrich-usecases-backend-brief.md.
// The window is stored relative (rolling "last N days") so the list re-resolves daily.
export const NewInRoleSchema = z
	.object({
		changeTypes: z.array(z.nativeEnum(NewInRoleChangeType)).optional(),
		window: z
			.object({
				preset: z.enum(["30", "90", "180", "365", "custom"]).optional(),
				fromDaysAgo: z.number().optional().nullable(),
				toDaysAgo: z.number().optional().nullable(),
			})
			.optional()
			.nullable(),
	})
	.nullable()
	.optional();

// Exclude companies — see docs/company-enrich-usecases-backend-brief.md.
// Subtracts companies before the list is built (by scope/lists, competitors, or domains).
export const ExcludeCompaniesSchema = z
	.object({
		scope: z.enum(["known", "lists"]).nullable().optional(),
		listIds: z.array(z.number()).optional(),
		excludeCompetitors: z.boolean().optional(),
	})
	.nullable()
	.optional();

// Person-level education filter — free-text university/institution name queries.
export const PersonEducationFilterSchema = z
	.object({
		institutionNameQuery: z.array(z.string()).optional(),
	})
	.nullable()
	.optional();

const RangeFilterSchema = z
	.object({
		min: z.number().optional().nullable(),
		max: z.number().optional().nullable(),
	})
	.optional();

const WorkforceGrowthSchema = z
	.object({
		period: z.enum(["m6", "y1", "y2"]).nullable().optional(),
		department: z
			.enum([
				"overall",
				"c_suite",
				"product_management",
				"engineering_technical",
				"design",
				"education",
				"finance",
				"human_resources",
				"information_technology",
				"legal",
				"marketing",
				"medical_health",
				"operations",
				"sales",
				"consulting",
			])
			.nullable()
			.optional(),
		minPercent: z.number().nullable().optional(),
		maxPercent: z.number().nullable().optional(),
	})
	.refine(
		(data) => {
			const hasBounds = data.minPercent != null || data.maxPercent != null;

			return !hasBounds || data.period != null;
		},
		{
			message: "Period is required when workforce growth bounds are set",
			path: ["period"],
		}
	)
	.refine(
		(data) => {
			const hasBounds = data.minPercent != null || data.maxPercent != null;

			return !hasBounds || data.department != null;
		},
		{
			message: "Department is required when workforce growth bounds are set",
			path: ["department"],
		}
	)
	.nullable()
	.optional();

export const IndustrySchema = z.object({
	name: z.string(),
	naicsCodes: z.array(z.number()),
});

export const CompanyFilterSearchSchema = z.object({
	lists: z.array(z.string()).optional(),
	semanticQuery: z.string().optional(),
	semanticWeight: z.number().optional(),
	exclude: z
		.object({
			industries: z.array(IndustrySchema).optional(),
			employees: z.array(z.string()).optional(),
			revenue: z.array(z.string()).optional(),
			keywords: z.array(z.string()).optional(),
			technologies: z.array(z.string()).optional(),
		})
		.optional(),
	query: z.string().optional(),
	foundedYear: RangeFilterSchema,
	fundingAmount: RangeFilterSchema,
	fundingYear: RangeFilterSchema,
	categoryOperator: z.enum(["And", "Or"]).optional(),
	keywordsOperator: z.enum(["And", "Or"]).optional(),
	technologiesOperator: z.enum(["And", "Or"]).optional(),
	require: z.array(z.string()).optional(),
	regions: z.array(z.string()).optional(),
	countries: z.array(z.string()).optional(),
	states: z.array(z.number()).optional(),
	cities: z.array(z.number()).optional(),
	type: z.array(z.string()).optional(),
	category: z.array(z.string()).optional(),
	industries: z.array(IndustrySchema).optional(),
	employees: z.array(z.string()).optional(),
	revenue: z.array(z.string()).optional(),
	keywords: z.array(z.string()).optional(),
	technologies: z.array(z.string()).optional(),
	fundingRounds: z.array(z.string()).optional(),
	workforceGrowth: WorkforceGrowthSchema,
});

export const CompanyFilterSimilarSchema = CompanyFilterSearchSchema.extend({
	domains: z.array(z.string()).optional(),
	similarDomains: z.array(z.string()).optional(),
	similarityWeight: z.number().optional(),
});

export const CompanyEnrichSchema = z.object({
	id: z.number(),
	name: z.string().min(2, { message: REQUIRED }).trim(),
	type: z.nativeEnum(CompanyEnrichType).optional(),
	newInRole: NewInRoleSchema,
	excludeCompanies: ExcludeCompaniesSchema,
	companyId: z.number().optional(),
	query: z.string().nullable().optional(),
	positionQuery: z.array(z.string()).nullable().optional(),
	atCurrentCompanyAfter: z.string().nullable().optional(),
	atCurrentCompanyBefore: z.string().nullable().optional(),
	atCurrentPositionAfter: z.string().nullable().optional(),
	atCurrentPositionBefore: z.string().nullable().optional(),
	countries: z.array(z.string()).nullable().optional(),
	domains: z.array(z.string()).nullable().optional(),
	seniority: z.array(z.string()).nullable().optional(),
	department: z.array(z.string()).nullable().optional(),
	education: PersonEducationFilterSchema,
	excludePositionQuery: z.array(z.string()).nullable().optional(),
	excludeCountries: z.array(z.string()).nullable().optional(),
	excludeDomains: z.array(z.string()).nullable().optional(),
	excludeSeniority: z.array(z.string()).nullable().optional(),
	excludeDepartment: z.array(z.string()).nullable().optional(),
	companyCountries: z.array(z.string()).nullable().optional(),
	companyStates: z.array(z.number()).nullable().optional(),
	companyCities: z.array(z.number()).nullable().optional(),
	companyFilterSearch: CompanyFilterSearchSchema.nullable().optional(),
	companyFilterSimilar: CompanyFilterSimilarSchema.nullable().optional(),
	lists: z
		.array(z.object({ id: z.number(), name: z.string() }))
		.nullable()
		.optional(),
	createdAt: z.string().optional(),
	updatedAt: z.string().optional(),
});

const CreateCompanyEnrichObjectSchema = z.object({
	id: z.number().optional(),
	// Preview-only paging: the preview endpoint returns a single page, so asking
	// for one switches the backend to the paged people search.
	page: z.number().optional(),
	pageSize: z.number().optional(),
	listId: z.number(),
	name: z.string().min(2, { message: REQUIRED }).trim(),
	type: z.nativeEnum(CompanyEnrichType).optional(),
	newInRole: NewInRoleSchema,
	excludeCompanies: ExcludeCompaniesSchema,
	query: z.string().nullable().optional(),
	positionQuery: z.array(z.string()).nullable().optional(),
	atCurrentCompanyAfter: z.string().nullable().optional(),
	atCurrentCompanyBefore: z.string().nullable().optional(),
	atCurrentPositionAfter: z.string().nullable().optional(),
	atCurrentPositionBefore: z.string().nullable().optional(),
	countries: z.array(z.string()).nullable().optional(),
	domains: z.array(z.string()).nullable().optional(),
	seniority: z.array(z.string()).nullable().optional(),
	department: z.array(z.string()).nullable().optional(),
	education: PersonEducationFilterSchema,
	excludePositionQuery: z.array(z.string()).nullable().optional(),
	excludeCountries: z.array(z.string()).nullable().optional(),
	excludeDomains: z.array(z.string()).nullable().optional(),
	excludeSeniority: z.array(z.string()).nullable().optional(),
	excludeDepartment: z.array(z.string()).nullable().optional(),
	companyCountries: z.array(z.string()).nullable().optional(),
	companyStates: z.array(z.number()).nullable().optional(),
	companyCities: z.array(z.number()).nullable().optional(),
	companyFilterSearch: CompanyFilterSearchSchema.nullable().optional(),
	companyFilterSimilar: CompanyFilterSimilarSchema.nullable().optional(),
});

export const CreateCompanyEnrichSchema = CreateCompanyEnrichObjectSchema.superRefine(
	(data, ctx) => {
		if (data.type === CompanyEnrichType.LOOK_A_LIKE) {
			const lookalikeDomains = data.companyFilterSimilar?.domains;

			if (!lookalikeDomains?.length) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: REQUIRED,
					path: ["companyFilterSimilar", "domains"],
				});
			}

			if (lookalikeDomains && lookalikeDomains.length > MAX_LOOKALIKE_DOMAINS) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: `A maximum of ${MAX_LOOKALIKE_DOMAINS} similar domains is allowed`,
					path: ["companyFilterSimilar", "domains"],
				});
			}
		}

		if ((data.domains?.length ?? 0) > MAX_COMPANY_DOMAINS) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: `A maximum of ${MAX_COMPANY_DOMAINS} domains is allowed`,
				path: ["domains"],
			});
		}

		if (data.type === CompanyEnrichType.NEW_IN_ROLE) {
			if (!data.newInRole?.changeTypes?.length) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: REQUIRED,
					path: ["newInRole", "changeTypes"],
				});
			}

			const window = data.newInRole?.window;
			const hasWindow =
				!!window && (!!window.preset || window.fromDaysAgo != null || window.toDaysAgo != null);

			if (!hasWindow) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: REQUIRED,
					path: ["newInRole", "window"],
				});
			}
		}
	}
);

export const CompanyEnrichPreviewExperienceCompanySchema = z.object({
	id: z.string(),
	name: z.string().nullable().optional(),
	domain: z.string().nullable().optional(),
	industry: z.string().nullable().optional(),
	logo_url: z.string().nullable().optional(),
});

export const CompanyEnrichPreviewExperienceSchema = z.object({
	is_current: z.boolean(),
	is_matched: z.boolean(),
	position: z.string().nullable().optional(),
	company: CompanyEnrichPreviewExperienceCompanySchema.nullable().optional(),
});

export const CompanyEnrichPreviewPersonSchema = z.object({
	id: z.number(),
	name: z.string(),
	experiences: z.array(CompanyEnrichPreviewExperienceSchema).nullable().optional(),
	position: z.string().nullable().optional(),
	seniority: z.string().nullable().optional(),
	department: z.string().nullable().optional(),
	image_url: z.string().nullable().optional(),
});

export const ResolvePositionsRequestSchema = z.object({ roles: z.array(z.string()) });
export const ResolvePositionsResponseSchema = z.object({ titles: z.array(z.string()) });
export type ResolvePositionsRequest = z.infer<typeof ResolvePositionsRequestSchema>;
export type ResolvePositionsResponse = z.infer<typeof ResolvePositionsResponseSchema>;

export const CompanyEnrichPreviewResponseSchema = z.object({
	items: z.array(CompanyEnrichPreviewPersonSchema),
	totalItems: z.number(),
});

// Company-only preview schemas
export const CompanySearchPreviewItemSchema = z.object({
	id: z.string(),
	name: z.string().nullable().optional(),
	domain: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	website: z.string().nullable().optional(),
	industry: z.string().nullable().optional(),
	keywords: z.array(z.string()).nullable().optional(),
	logo_url: z.string().nullable().optional(),
	updated_at: z.string().nullable().optional(),
});

export const CompanySearchPreviewResponseSchema = z.object({
	items: z.array(CompanySearchPreviewItemSchema),
	totalItems: z.number(),
});

export const CompanySearchPreviewInputSchema = CompanyFilterSearchSchema.extend({
	listId: z.number().optional(),
});

export const CompanyEnrichResponseSchema = z.object({
	data: z.array(CompanyEnrichSchema),
	meta: MetaDataSchema,
});

// Lookup schemas
export const GeoRegionSchema = z.object({
	id: z.string(),
	parentId: z.string().nullable(),
	name: z.string(),
	countries: z.array(z.string()),
});

export const GeoRegionResponseSchema = z.object({
	items: z.array(GeoRegionSchema),
});

export const GeoCountrySchema = z.object({
	code: z.string(),
	name: z.string(),
	latitude: z.number().optional(),
	longitude: z.number().optional(),
});

export const GeoCountryResponseSchema = z.object({
	items: z.array(GeoCountrySchema),
	page: z.number(),
	totalPages: z.number(),
	totalItems: z.number(),
});

export const GeoStateSchema = z.object({
	id: z.number(),
	name: z.string(),
});

export const GeoStateResponseSchema = z.object({
	items: z.array(GeoStateSchema),
	page: z.number(),
	totalPages: z.number(),
	totalItems: z.number(),
});

export const GeoCitySchema = z.object({
	id: z.number(),
	name: z.string(),
});

export const GeoCityResponseSchema = z.object({
	items: z.array(GeoCitySchema),
	page: z.number(),
	totalPages: z.number(),
	totalItems: z.number(),
});

export const AutocompleteItemSchema = z.object({
	id: z.string(),
	name: z.string(),
});

export const CompanyAutoCompleteInfoSchema = z.object({
	domain: z.string(),
	name: z.string().nullable(),
	logoUrl: z.string().nullable(),
});

export const CompanyEnrichPersonDetailSchema = z.object({
	id: z.number(),
	name: z.string(),
	first_name: z.string().nullable().optional(),
	last_name: z.string().nullable().optional(),
	position: z.string().nullable().optional(),
	image_url: z.string().nullable().optional(),
	location: z
		.object({
			country: z
				.object({
					code: z.string().nullable().optional(),
					name: z.string().nullable().optional(),
					latitude: z.number().nullable().optional(),
					longitude: z.number().nullable().optional(),
				})
				.nullable()
				.optional(),
			address: z.string().nullable().optional(),
		})
		.nullable()
		.optional(),
	socials: z
		.object({
			linkedin_url: z.string().nullable().optional(),
		})
		.nullable()
		.optional(),
	experiences: z
		.array(
			z.object({
				type: z.string(),
				startDate: z.string().nullable().optional(),
				endDate: z.string().nullable().optional(),
				isCurrent: z.boolean().nullable().optional(),
				isMatched: z.boolean().nullable().optional(),
				position: z.string().nullable().optional(),
				company: z
					.object({
						id: z.number(),
						name: z.string(),
						domain: z.string().nullable().optional(),
						logo_url: z.string().nullable().optional(),
					})
					.nullable()
					.optional(),
				companyName: z.string().nullable().optional(),
			})
		)
		.nullable()
		.optional(),
});

/** Lean upstream company detail (GET company-enrich/companies/:id/detail). */
export const CompanyEnrichCompanyDetailSchema = z.object({
	id: z.string(),
	name: z.string().nullable(),
	domain: z.string().nullable(),
	website: z.string().nullable(),
	linkedinUrl: z.string().nullable(),
	logoUrl: z.string().nullable(),
});

export type CompanyEnrichCompanyDetail = z.infer<typeof CompanyEnrichCompanyDetailSchema>;

export type CompanyEnrich = z.infer<typeof CompanyEnrichSchema>;
export type CreateCompanyEnrich = z.infer<typeof CreateCompanyEnrichSchema>;
export type NewInRole = z.infer<typeof NewInRoleSchema>;
export type PersonEducationFilter = z.infer<typeof PersonEducationFilterSchema>;
export type ExcludeCompanies = z.infer<typeof ExcludeCompaniesSchema>;
export type CompanyEnrichResponse = z.infer<typeof CompanyEnrichResponseSchema>;
export type CompanyEnrichPreviewResponse = z.infer<typeof CompanyEnrichPreviewResponseSchema>;
export type CompanyEnrichPreviewPerson = z.infer<typeof CompanyEnrichPreviewPersonSchema>;
export type CompanyEnrichPreviewExperience = z.infer<typeof CompanyEnrichPreviewExperienceSchema>;
export type CompanyEnrichPreviewExperienceCompany = z.infer<
	typeof CompanyEnrichPreviewExperienceCompanySchema
>;
export type CompanySearchPreviewItem = z.infer<typeof CompanySearchPreviewItemSchema>;
export type CompanySearchPreviewResponse = z.infer<typeof CompanySearchPreviewResponseSchema>;
export type CompanySearchPreviewInput = z.infer<typeof CompanySearchPreviewInputSchema>;
export type CompanyEnrichPersonDetail = z.infer<typeof CompanyEnrichPersonDetailSchema>;
export type CompanyFilterSearch = z.infer<typeof CompanyFilterSearchSchema>;
export type CompanyFilterSimilar = z.infer<typeof CompanyFilterSimilarSchema>;
export type GeoRegion = z.infer<typeof GeoRegionSchema>;
export type GeoRegionResponse = z.infer<typeof GeoRegionResponseSchema>;
export type GeoCountry = z.infer<typeof GeoCountrySchema>;
export type GeoCountryResponse = z.infer<typeof GeoCountryResponseSchema>;
export type GeoState = z.infer<typeof GeoStateSchema>;
export type GeoStateResponse = z.infer<typeof GeoStateResponseSchema>;
export type GeoCity = z.infer<typeof GeoCitySchema>;
export type GeoCityResponse = z.infer<typeof GeoCityResponseSchema>;
export type Industry = z.infer<typeof IndustrySchema>;
export type AutocompleteItem = z.infer<typeof AutocompleteItemSchema>;
export type CompanyAutoCompleteInfo = z.infer<typeof CompanyAutoCompleteInfoSchema>;
